import React, { useCallback, useRef, useState } from 'react'
import { Layout } from 'src/antd'
import styled from 'styled-components'

import { useDesignMode } from 'src/design-mode'
import { blue600 } from 'src/styles/color-theme'

import NavigationMenu from './components/NavigationMenu'

const { Sider } = Layout

/**
 * The sider's two widths. Exported because the `?design` app shell pins its
 * own sidebar COLUMN to them (`components/main/shell`): there the Sider is one
 * block in a flex column, and a column that sizes itself to its widest child
 * would be decided by the brand or the user block rather than by the nav.
 */
export const SIDER_WIDTH = 256
export const SIDER_COLLAPSED_WIDTH = 64

/**
 * The sider surface with the design toggle ON — sidebar fix 17 on
 * `preview/design-toggle-all`, a captain's ruling carried across verbatim:
 * the sidebar sits on zinc-100, the shadcn preset generator's own page
 * background for Base Color Zinc. `--muted` already IS that value
 * (`oklch(0.967 0.001 286.375)`), so this reads the TOKEN rather than a hex,
 * and dark inverts on its own — dark's `--muted` is `oklch(0.274 …)`, one
 * step lighter than dark's `--background`, the same relationship light has.
 *
 * It is load-bearing rather than decorative: the nav's selected row is a
 * `--background` pill (`src/antd/Menu.shadcn.tsx`), and `--background` is
 * `oklch(1 0 0)` — the same white as `bgSider`. On the old surface that pill
 * would be invisible. The two changes are a pair.
 */
const SHADCN_SIDER_BG = 'var(--muted)'

const StyledSider = styled(Sider)<{ $shadcn: boolean; $draggable: boolean }>`
  min-height: 100%;
  padding: 0 0;
  background-color: ${(p) =>
    p.$shadcn ? SHADCN_SIDER_BG : p.theme.bgSider} !important;
  border-top: 1px solid ${(p) => p.theme.gray400};
  position: relative;
  /* The col-resize cursor was on the WHOLE sider, not just the 4px handle —
     which is why the entire sidebar advertised a drag. With the toggle on
     there is no drag, so there is no cursor either (sidebar fix 14). */
  cursor: ${({ $draggable }) => ($draggable ? 'col-resize' : 'default')};

  &:hover {
    cursor: ${({ $draggable }) => ($draggable ? 'col-resize' : 'default')};
  }
`

const ResizeHandle = styled.div<{ $isDragging: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  width: 4px;
  height: 100%;
  background: transparent;
  cursor: col-resize;
  z-index: 1000;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${(p) => p.theme.primary || blue600};
    opacity: 0.5;
  }

  ${({ $isDragging }) =>
    $isDragging &&
    `
    background-color: ${blue600};
    opacity: 0.8;
  `}

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -2px;
    right: -2px;
    height: 100%;
  }
`

type TSidebarProps = {
  openedKey?: string
  collapsed: boolean
  onToggleSidebar?: () => void
}

const Sidebar: React.FC<TSidebarProps> = ({
  openedKey,
  collapsed,
  onToggleSidebar,
}) => {
  /**
   * Sidebar fix 14, a captain's call carried across from
   * `preview/design-toggle-all`: with the toggle on the sidebar is not
   * draggable — width changes come from the collapse control alone.
   *
   * Worth knowing what this drag actually was: it never resized anything. It
   * measured the pointer and, past ±30px, called `onToggleSidebar()` — a
   * snap-to-collapse gesture wearing a resize cursor. No width was ever
   * computed from it and none was ever stored (`sidebarStartWidth` below is
   * assigned and never read), so switching it off takes nothing else with it.
   * Toggle OFF keeps the gesture, the handle and the cursor exactly as they
   * are today.
   */
  const shadcn = useDesignMode() === 'shadcn'
  const draggable = !shadcn
  const [isDragging, setIsDragging] = useState(false)
  const dragStartX = useRef<number>(0)
  const sidebarStartWidth = useRef<number>(0)

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!draggable) return
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(true)
      dragStartX.current = e.clientX
      sidebarStartWidth.current = collapsed
        ? SIDER_COLLAPSED_WIDTH
        : SIDER_WIDTH

      let hasSnapped = false
      let mouseMoveHandler: ((event: MouseEvent) => void) | null = null
      let mouseUpHandler: ((event: MouseEvent) => void) | null = null

      const cleanup = () => {
        setIsDragging(false)
        if (mouseMoveHandler) {
          document.removeEventListener('mousemove', mouseMoveHandler)
        }
        if (mouseUpHandler) {
          document.removeEventListener('mouseup', mouseUpHandler)
        }
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }

      mouseMoveHandler = (moveEvent: MouseEvent) => {
        if (hasSnapped) return

        const deltaX = moveEvent.clientX - dragStartX.current
        const snapThreshold = 30

        if (collapsed && deltaX > snapThreshold && onToggleSidebar) {
          onToggleSidebar()
          hasSnapped = true
          cleanup()
        } else if (!collapsed && deltaX < -snapThreshold && onToggleSidebar) {
          onToggleSidebar()
          hasSnapped = true
          cleanup()
        }
      }

      mouseUpHandler = () => {
        cleanup()
      }

      document.addEventListener('mousemove', mouseMoveHandler)
      document.addEventListener('mouseup', mouseUpHandler)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    },
    [collapsed, draggable, onToggleSidebar]
  )

  return (
    <StyledSider
      $shadcn={shadcn}
      $draggable={draggable}
      collapsedWidth={SIDER_COLLAPSED_WIDTH}
      width={SIDER_WIDTH}
      trigger={null}
      collapsed={collapsed}
      data-test="sidebar"
      // The main module menu repeats on every page — the omnisearch quick
      // actions scan skips it (other navigation stays included).
      data-omni-ignore=""
      onMouseDown={draggable ? handleMouseDown : undefined}
    >
      <NavigationMenu openedKey={openedKey} collapsed={collapsed} />
      {draggable && (
        <ResizeHandle
          $isDragging={isDragging}
          onMouseDown={handleMouseDown}
          data-test="sidebar-resize-handle"
        />
      )}
    </StyledSider>
  )
}

export default Sidebar
